/**
 * Copyright 2017 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import { createStore, applyMiddleware, compose } from "redux";
import { loggedUserReducer } from "openstack-uicore-foundation/lib/security/reducers";
import thunk from "redux-thunk";
import { persistStore, persistCombineReducers } from "redux-persist";
import storage from "redux-persist/es/storage";
import { allFiltersReducer } from "openstack-uicore-foundation/lib/components/mui/grid-filter";
import baseReducer from "./reducers/base-reducer";
import currentSummitReducer from "./reducers/summits/current-summit-reducer";
import directoryReducer from "./reducers/summits/directory-reducer";
import scheduleBuilderReducer from "./reducers/events/schedule-builder-reducer";
import eventListReducer from "./reducers/events/event-list-reducer";
import summitEventReducer from "./reducers/events/summit-event-reducer";
import speakerListReducer from "./reducers/speakers/speaker-list-reducer";
import speakerReducer from "./reducers/speakers/speaker-reducer";
import speakerMergeReducer from "./reducers/speakers/speaker-merge-reducer";
import summitEventBulkActionReducer from "./reducers/events/summit-event-bulk-actions-reducer";
import attendeeListReducer from "./reducers/attendees/attendee-list-reducer";
import attendeeReducer from "./reducers/attendees/attendee-reducer";
import promocodeListReducer from "./reducers/promocodes/promocode-list-reducer";
import promocodeReducer from "./reducers/promocodes/promocode-reducer";
import promocodeSpecificationReducer from "./reducers/promocodes/promocode-specification-reducer";
import speakerAttendanceListReducer from "./reducers/summit_speakers/speaker-attendance-list-reducer";
import speakerAttendanceReducer from "./reducers/summit_speakers/speaker-attendance-reducer";
import eventTypeListReducer from "./reducers/events/event-type-list-reducer";
import eventTypeReducer from "./reducers/events/event-type-reducer";
import eventCategoryListReducer from "./reducers/events/event-category-list-reducer";
import eventCategoryReducer from "./reducers/events/event-category-reducer";
import eventCategoryGroupListReducer from "./reducers/events/event-category-group-list-reducer";
import eventCategoryGroupReducer from "./reducers/events/event-category-group-reducer";
import eventMaterialReducer from "./reducers/events/event-material-reducer";
import locationListReducer from "./reducers/locations/location-list-reducer";
import locationReducer from "./reducers/locations/location-reducer";
import floorReducer from "./reducers/locations/floor-reducer";
import roomReducer from "./reducers/locations/room-reducer";
import locationImageReducer from "./reducers/locations/location-image-reducer";
import locationMapReducer from "./reducers/locations/location-map-reducer";
import rsvpTemplateListReducer from "./reducers/rsvps/rsvp-template-list-reducer";
import rsvpTemplateReducer from "./reducers/rsvps/rsvp-template-reducer";
import rsvpQuestionReducer from "./reducers/rsvps/rsvp-question-reducer";
import ticketTypeListReducer from "./reducers/tickets/ticket-type-list-reducer";
import ticketTypeReducer from "./reducers/tickets/ticket-type-reducer";
import taxTypeListReducer from "./reducers/taxes/tax-type-list-reducer";
import taxTypeReducer from "./reducers/taxes/tax-type-reducer";
import pushNotificationListReducer from "./reducers/push_notifications/push-notification-list-reducer";
import pushNotificationReducer from "./reducers/push_notifications/push-notification-reducer";
import selectionPlanListReducer from "./reducers/selection_plans/selection-plan-list-reducer";
import selectionPlanReducer from "./reducers/selection_plans/selection-plan-reducer";
import roomOccupancyReducer from "./reducers/events/room-occupancy-reducer";
import tagGroupListReducer from "./reducers/tags/tag-group-list-reducer";
import tagGroupReducer from "./reducers/tags/tag-group-reducer";
import reportReducer from "./reducers/reports/report-reducer";
import roomBookingReducer from "./reducers/room_bookings/room-booking-reducer";
import roomBookingListReducer from "./reducers/room_bookings/room-booking-list-reducer";
import roomBookingAttributeTypeReducer from "./reducers/room_bookings/room-booking-attribute-type-reducer";
import badgeTypeListReducer from "./reducers/badges/badge-type-list-reducer";
import badgeTypeReducer from "./reducers/badges/badge-type-reducer";
import viewTypeListReducer from "./reducers/badges/view-type-list-reducer";
import viewTypeReducer from "./reducers/badges/view-type-reducer";
import badgeFeatureListReducer from "./reducers/badges/badge-feature-list-reducer";
import badgeFeatureReducer from "./reducers/badges/badge-feature-reducer";
import badgeSettingsReducer from "./reducers/badges/badge-settings-reducer";
import accessLevelListReducer from "./reducers/badges/access-level-list-reducer";
import accessLevelReducer from "./reducers/badges/access-level-reducer";
import orderExtraQuestionListReducer from "./reducers/orders/order-extra-question-list-reducer";
import orderExtraQuestionReducer from "./reducers/orders/order-extra-question-reducer";
import purchaseOrderListReducer from "./reducers/orders/purchase-order-list-reducer";
import purchaseOrderReducer from "./reducers/orders/purchase-order-reducer";
import sponsorListReducer from "./reducers/sponsors/sponsor-list-reducer";
import sponsorReducer from "./reducers/sponsors/sponsor-reducer";
import sponsorFormsListReducer from "./reducers/sponsors/sponsor-forms-list-reducer";
import sponsorFormItemsListReducer from "./reducers/sponsors/sponsor-form-items-list-reducer";
import sponsorUsersListReducer from "./reducers/sponsors/sponsor-users-list-reducer";
import sponsorPromocodeListReducer from "./reducers/sponsors/promocode-list-reducer";
import sponsorExtraQuestionReducer from "./reducers/sponsors/sponsor-extra-question-reducer";
import sponsorshipListReducer from "./reducers/sponsorships/sponsorship-list-reducer";
import sponsorshipReducer from "./reducers/sponsorships/sponsorship-reducer";
import summitSponsorshipListReducer from "./reducers/sponsors/summit-sponsorship-list-reducer";
import summitSponsorshipReducer from "./reducers/sponsors/summit-sponsorship-reducer";
import refundPolicyListReducer from "./reducers/refund_policies/refund-policy-list-reducer";
import paymentProfileListReducer from "./reducers/payment_profiles/payment-profile-list-reducer";
import ticketListReducer from "./reducers/tickets/ticket-list-reducer";
import ticketReducer from "./reducers/tickets/ticket-reducer";
import badgeScansListReducer from "./reducers/sponsors/badge-scans-list-reducer";
import marketingSettingListReducer from "./reducers/marketing/marketing-setting-list-reducer";
import marketingSettingReducer from "./reducers/marketing/marketing-setting-reducer";
import paymentProfileReducer from "./reducers/payment_profiles/payment-profile-reducer";
import paymentFeeTypeReducer from "./reducers/payment_profiles/payment-fee-type-reducer.js";
import paymentFeeTypesListReducer from "./reducers/payment_profiles/payment-fee-types-list-reducer.js";
import emailTemplateReducer from "./reducers/emails/email-template-reducer";
import emailTemplateListReducer from "./reducers/emails/email-template-list-reducer";
import emailLogListReducer from "./reducers/emails/email-log-list-reducer";
import summitDocListReducer from "./reducers/summitdoc/summitdoc-list-reducer";
import summitDocReducer from "./reducers/summitdoc/summitdoc-reducer";
import emailFlowEventsListReducer from "./reducers/email_flow_events/email-flow-events-list-reducer";
import emailFlowEventReducer from "./reducers/email_flow_events/email-flows-event-reducer";
import RegistrationInvitationListReducer from "./reducers/tickets/registration-invitation-list-reducer";
import adminAccessListReducer from "./reducers/admin_access/admin-access-list-reducer";
import adminAccessReducer from "./reducers/admin_access/admin-access-reducer";
import mediaFileTypeListReducer from "./reducers/media_file_types/media-file-type-list-reducer";
import mediaFileTypeReducer from "./reducers/media_file_types/media-file-type-reducer";
import mediaUploadListReducer from "./reducers/media_uploads/media-uploads-list-reducer";
import mediaUploadReducer from "./reducers/media_uploads/media-upload-reducer";
import registrationInvitationReducer from "./reducers/tickets/registration-invitation-reducer";
import companyListReducer from "./reducers/companies/company-list-reducer";
import companyReducer from "./reducers/companies/company-reducer";
import tagListReducer from "./reducers/tags/tag-list-reducer";
import tagReducer from "./reducers/tags/tag-reducer";
import featuredSpeakersReducer from "./reducers/summit_speakers/featured-speakers-reducer";
import trackChairListReducer from "./reducers/track_chairs/track-chair-list-reducer";
import teamListsReducer from "./reducers/track_chairs/team-lists-reducer";
import progressFlagsReducer from "./reducers/track_chairs/progress-flags-reducer";
import sponsoredProjectListReducer from "./reducers/sponsored_projects/sponsored-project-list-reducer";
import sponsoredProjectReducer from "./reducers/sponsored_projects/sponsored-project-reducer";
import sponsoredProjectSponsorshipTypeReducer from "./reducers/sponsored_projects/sponsored-project-sponsorship-type-reducer";
import sponsoredProjectSponsorshipTypeSupportingCompanyReducer from "./reducers/sponsored_projects/sponsored-project-sponsorship-type-supporting-company-reducer";
import scheduleSettingsReducer from "./reducers/schedule-settings/schedule-settings-reducer";
import scheduleSettingsListReducer from "./reducers/schedule-settings/schedule-settings-list-reducer";
import selectionPlanExtraQuestionReducer from "./reducers/selection_plans/selection-plan-extra-question-reducer";
import registrationCompanyListReducer from "./reducers/companies/registration-companies-list-reducer";
import orderExtraQuestionRuleReducer from "./reducers/orders/order-extra-question-rule-reducer";
import presentationVotesReducer from "./reducers/events/summit-presentation-votes-reducer";
import ratingTypeReducer from "./reducers/ranking/rating-type-reducer";
import scoreTypeReducer from "./reducers/ranking/score-type-reducer";
import summitSpeakersListReducer from "./reducers/summit_speakers/summit-speakers-list-reducer";
import summitSubmittersListReducer from "./reducers/summit_submitters/summit-submitters-list-reducer";
import summitStatsReducer from "./reducers/summits/summit-stats-reducer";
import sponsorAdvertisementReducer from "./reducers/sponsors/sponsor-advertisement-reducer";
import sponsorMaterialReducer from "./reducers/sponsors/sponsor-material-reducer";
import sponsorSocialNetworkReducer from "./reducers/sponsors/sponsor-social-network-reducer";
import auditLogReducer from "./reducers/audit_log/audit-log-reducer";
import signageReducer from "./reducers/signage/signage-reducer";
import eventCommentReducer from "./reducers/events/event-comment-reducer";
import SubmmissionInvitationListReducer from "./reducers/speakers/submission-invitation-list-reducer";
import submissionInvitationReducer from "./reducers/speakers/submission-invitation-reducer";
import trackTimeframesListReducer from "./reducers/track_chairs/track-timeframes-list-reducer";
import trackTimeframeReducer from "./reducers/track_chairs/track-timeframe-reducer";
import currentRegFeedMetadataListReducer from "./reducers/summits/reg-feed-metadata-list-reducer";
import currentRegFeedMetadataReducer from "./reducers/summits/reg-feed-metadata-reducer";
import badgePrintReducer from "./reducers/tickets/badge-print-reducer";
import notesReducer from "./reducers/notes/notes-reducer";
import emailFlowEventSettingsReducer from "./reducers/email_flow_events/email-flows-event-settings-reducer";
import badgeScanReducer from "./reducers/sponsors/badge-scan-reducer";
import inventoryItemReducer from "./reducers/sponsors_inventory/inventory-item-reducer";
import inventoryItemListReducer from "./reducers/sponsors_inventory/inventory-item-list-reducer";
import formTemplateReducer from "./reducers/sponsors_inventory/form-template-reducer.js";
import formTemplateListReducer from "./reducers/sponsors_inventory/form-template-list-reducer.js";
import formTemplateItemReducer from "./reducers/sponsors_inventory/form-template-item-reducer.js";
import formTemplateItemListReducer from "./reducers/sponsors_inventory/form-template-item-list-reducer.js";
import pageTemplateListReducer from "./reducers/sponsors_inventory/page-template-list-reducer.js";
import pageTemplateReducer from "./reducers/sponsors_inventory/page-template-reducer.js";
import sponsorSettingsReducer from "./reducers/sponsor_settings/sponsor-settings-reducer";
import eventRSVPListReducer from "./reducers/rsvps/event-rsvp-list-reducer.js";
import eventRSVPInvitationListReducer from "./reducers/rsvps/event-rsvp-invitation-list-reducer.js";
import eventRSVPReducer from "./reducers/events/event-rsvp-reducer.js";
import sponsorPageFormsListReducer from "./reducers/sponsors/sponsor-page-forms-list-reducer.js";
import sponsorCustomizedFormReducer from "./reducers/sponsors/sponsor-customized-form-reducer.js";
import sponsorPageCartListReducer from "./reducers/sponsors/sponsor-page-cart-list-reducer";
import sponsorCustomizedFormItemsListReducer from "./reducers/sponsors/sponsor-customized-form-items-list-reducer.js";
import showPagesListReducer from "./reducers/sponsors/show-pages-list-reducer.js";
import showPurchaseListReducer from "./reducers/sponsors/show-purchase-list-reducer.js";
import sponsorPagePurchaseListReducer from "./reducers/sponsors/sponsor-page-purchase-list-reducer.js";
import sponsorPagePagesListReducer from "./reducers/sponsors/sponsor-page-pages-list-reducer.js";
import sponsorPageMUListReducer from "./reducers/sponsors/sponsor-page-mu-list-reducer.js";
import dropboxSyncReducer from "./reducers/locations/dropbox-sync-reducer";
import sponsorReportsPurchaseDetailsReducer from "./reducers/sponsors/sponsor-reports-purchase-details-reducer";
import sponsorReportsPurchaseDetailsLinesReducer from "./reducers/sponsors/sponsor-reports-purchase-details-lines-reducer";
import sponsorReportsSponsorAssetReducer from "./reducers/sponsors/sponsor-reports-sponsor-asset-reducer";
import sponsorReportsDrilldownReducer from "./reducers/sponsors/sponsor-reports-drilldown-reducer";
import sponsorReportsPurchaseDetailsByItemReducer from "./reducers/sponsors/sponsor-reports-purchase-details-by-item-reducer";
import addOnTypeReducer from "./reducers/sponsors_inventory/add-on-type-reducer.js";
import addOnTypesListReducer from "./reducers/sponsors_inventory/add-on-types-list-reducer.js";

// default: localStorage if web, AsyncStorage if react-native

const config = {
  key: "root",
  storage,
  blacklist: [
    "dropboxSyncState",
    "sponsorReportsPurchaseDetailsState",
    "sponsorReportsPurchaseDetailsLinesState",
    "sponsorReportsPurchaseDetailsByItemState",
    "sponsorReportsSponsorAssetState",
    "sponsorReportsDrilldownState"
  ]
};

const reducers = persistCombineReducers(config, {
  loggedUserState: loggedUserReducer,
  allGridFiltersState: allFiltersReducer,
  baseState: baseReducer,
  directoryState: directoryReducer,
  currentSummitState: currentSummitReducer,
  currentScheduleBuilderState: scheduleBuilderReducer,
  currentSummitEventState: summitEventReducer,
  currentEventListState: eventListReducer,
  currentSpeakerListState: speakerListReducer,
  currentSpeakerState: speakerReducer,
  currentSpeakerMergeState: speakerMergeReducer,
  summitEventsBulkActionsState: summitEventBulkActionReducer,
  currentAttendeeListState: attendeeListReducer,
  currentAttendeeState: attendeeReducer,
  currentPromocodeListState: promocodeListReducer,
  currentPromocodeState: promocodeReducer,
  currentPromocodeSpecificationState: promocodeSpecificationReducer,
  currentSummitSpeakersListState: summitSpeakersListReducer,
  currentSpeakerAttendanceListState: speakerAttendanceListReducer,
  currentSpeakerAttendanceState: speakerAttendanceReducer,
  currentSummitSubmittersListState: summitSubmittersListReducer,
  currentEventTypeListState: eventTypeListReducer,
  currentEventTypeState: eventTypeReducer,
  currentEventCategoryListState: eventCategoryListReducer,
  currentEventCategoryState: eventCategoryReducer,
  currentEventCategoryGroupListState: eventCategoryGroupListReducer,
  currentEventCategoryGroupState: eventCategoryGroupReducer,
  currentEventMaterialState: eventMaterialReducer,
  currentEventRSVPState: eventRSVPReducer,
  currentEventCommentState: eventCommentReducer,
  currentLocationListState: locationListReducer,
  currentLocationState: locationReducer,
  currentFloorState: floorReducer,
  currentRoomState: roomReducer,
  currentLocationImageState: locationImageReducer,
  currentLocationMapState: locationMapReducer,
  currentRsvpTemplateListState: rsvpTemplateListReducer,
  currentRsvpTemplateState: rsvpTemplateReducer,
  currentRsvpQuestionState: rsvpQuestionReducer,
  currentEventRsvpListState: eventRSVPListReducer,
  currentEventRsvpInvitationListState: eventRSVPInvitationListReducer,
  currentTicketTypeListState: ticketTypeListReducer,
  currentTicketTypeState: ticketTypeReducer,
  currentTaxTypeListState: taxTypeListReducer,
  currentTaxTypeState: taxTypeReducer,
  currentPushNotificationListState: pushNotificationListReducer,
  currentPushNotificationState: pushNotificationReducer,
  currentSelectionPlanListState: selectionPlanListReducer,
  currentSelectionPlanState: selectionPlanReducer,
  currentRoomOccupancyState: roomOccupancyReducer,
  currentTagGroupListState: tagGroupListReducer,
  currentTagGroupState: tagGroupReducer,
  currentReportState: reportReducer,
  currentRoomBookingState: roomBookingReducer,
  currentRoomBookingListState: roomBookingListReducer,
  currentRoomBookingAttributeTypeState: roomBookingAttributeTypeReducer,
  currentBadgeTypeListState: badgeTypeListReducer,
  currentBadgeTypeState: badgeTypeReducer,
  currentBadgeFeatureListState: badgeFeatureListReducer,
  currentBadgeFeatureState: badgeFeatureReducer,
  currentBadgeSettingState: badgeSettingsReducer,
  currentBadgePrintState: badgePrintReducer,
  currentAccessLevelListState: accessLevelListReducer,
  currentAccessLevelState: accessLevelReducer,
  currentViewTypeListState: viewTypeListReducer,
  currentViewTypeState: viewTypeReducer,
  currentOrderExtraQuestionListState: orderExtraQuestionListReducer,
  currentOrderExtraQuestionState: orderExtraQuestionReducer,
  currentOrderExtraQuestionRuleState: orderExtraQuestionRuleReducer,
  currentPurchaseOrderListState: purchaseOrderListReducer,
  currentPurchaseOrderState: purchaseOrderReducer,
  currentSponsorListState: sponsorListReducer,
  currentSponsorState: sponsorReducer,
  sponsorFormsListState: sponsorFormsListReducer,
  sponsorFormItemsListState: sponsorFormItemsListReducer,
  showPagesListState: showPagesListReducer,
  showPurchaseListState: showPurchaseListReducer,
  sponsorUsersListState: sponsorUsersListReducer,
  sponsorPageFormsListState: sponsorPageFormsListReducer,
  sponsorPageCartListState: sponsorPageCartListReducer,
  sponsorPagePagesListState: sponsorPagePagesListReducer,
  sponsorPageMUListState: sponsorPageMUListReducer,
  sponsorCustomizedFormState: sponsorCustomizedFormReducer,
  sponsorCustomizedFormItemsListState: sponsorCustomizedFormItemsListReducer,
  sponsorPagePurchaseListState: sponsorPagePurchaseListReducer,
  currentSponsorPromocodeListState: sponsorPromocodeListReducer,
  currentSponsorExtraQuestionState: sponsorExtraQuestionReducer,
  currentSponsorAdvertisementState: sponsorAdvertisementReducer,
  currentSponsorMaterialState: sponsorMaterialReducer,
  currentSponsorSocialNetworkState: sponsorSocialNetworkReducer,
  currentSummitSponsorshipListState: summitSponsorshipListReducer,
  currentSummitSponsorshipState: summitSponsorshipReducer,
  currentSponsorshipListState: sponsorshipListReducer,
  currentSponsorshipState: sponsorshipReducer,
  currentRefundPolicyListState: refundPolicyListReducer,
  currentPaymentProfileListState: paymentProfileListReducer,
  currentPaymentFeeTypeState: paymentFeeTypeReducer,
  currentPaymentFeeListTypeState: paymentFeeTypesListReducer,
  currentPaymentProfileState: paymentProfileReducer,
  currentTicketListState: ticketListReducer,
  currentTicketState: ticketReducer,
  badgeScansListState: badgeScansListReducer,
  currentBadgeScanState: badgeScanReducer,
  marketingSettingListState: marketingSettingListReducer,
  marketingSettingState: marketingSettingReducer,
  emailTemplateState: emailTemplateReducer,
  emailTemplateListState: emailTemplateListReducer,
  emailLogListState: emailLogListReducer,
  summitDocListState: summitDocListReducer,
  summitDocState: summitDocReducer,
  emailFlowEventsListState: emailFlowEventsListReducer,
  emailFLowEventState: emailFlowEventReducer,
  emailFLowEventSettingsState: emailFlowEventSettingsReducer,
  RegistrationInvitationListState: RegistrationInvitationListReducer,
  SubmmissionInvitationListState: SubmmissionInvitationListReducer,
  currentSubmissionInvitationState: submissionInvitationReducer,
  adminAccessListState: adminAccessListReducer,
  adminAccessState: adminAccessReducer,
  mediaFileTypeListState: mediaFileTypeListReducer,
  mediaFileTypeState: mediaFileTypeReducer,
  mediaUploadListState: mediaUploadListReducer,
  mediaUploadState: mediaUploadReducer,
  currentRegistrationInvitationState: registrationInvitationReducer,
  currentCompanyListState: companyListReducer,
  currentCompanyState: companyReducer,
  currentTagListState: tagListReducer,
  currentTagState: tagReducer,
  featuredSpeakersState: featuredSpeakersReducer,
  trackChairListState: trackChairListReducer,
  teamListsState: teamListsReducer,
  progressFlagsState: progressFlagsReducer,
  sponsoredProjectListState: sponsoredProjectListReducer,
  sponsoredProjectState: sponsoredProjectReducer,
  sponsoredProjectSponsorshipTypeState: sponsoredProjectSponsorshipTypeReducer,
  sponsoredProjectSponsorshipTypeSupportingCompanyState:
    sponsoredProjectSponsorshipTypeSupportingCompanyReducer,
  scheduleSettingsState: scheduleSettingsReducer,
  scheduleSettingsListState: scheduleSettingsListReducer,
  currentSelectionPlanExtraQuestionState: selectionPlanExtraQuestionReducer,
  currentPresentationVotesState: presentationVotesReducer,
  currentRegistrationCompanyListState: registrationCompanyListReducer,
  ratingTypeState: ratingTypeReducer,
  scoreTypeState: scoreTypeReducer,
  summitStatsState: summitStatsReducer,
  auditLogState: auditLogReducer,
  notesState: notesReducer,
  signageState: signageReducer,
  trackTimeframesListState: trackTimeframesListReducer,
  trackTimeframeState: trackTimeframeReducer,
  currentRegFeedMetadataListState: currentRegFeedMetadataListReducer,
  currentRegFeedMetadataState: currentRegFeedMetadataReducer,
  currentInventoryItemState: inventoryItemReducer,
  currentInventoryItemListState: inventoryItemListReducer,
  currentFormTemplateState: formTemplateReducer,
  currentFormTemplateListState: formTemplateListReducer,
  currentFormTemplateItemState: formTemplateItemReducer,
  currentFormTemplateItemListState: formTemplateItemListReducer,
  sponsorSettingsState: sponsorSettingsReducer,
  pageTemplateListState: pageTemplateListReducer,
  pageTemplateState: pageTemplateReducer,
  dropboxSyncState: dropboxSyncReducer,
  sponsorReportsPurchaseDetailsState: sponsorReportsPurchaseDetailsReducer,
  sponsorReportsPurchaseDetailsLinesState:
    sponsorReportsPurchaseDetailsLinesReducer,
  sponsorReportsPurchaseDetailsByItemState:
    sponsorReportsPurchaseDetailsByItemReducer,
  sponsorReportsSponsorAssetState: sponsorReportsSponsorAssetReducer,
  sponsorReportsDrilldownState: sponsorReportsDrilldownReducer,
  currentAddOnTypesListState: addOnTypesListReducer,
  currentAddOnTypeState: addOnTypeReducer,
});

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const store = createStore(reducers, composeEnhancers(applyMiddleware(thunk)));

const onRehydrateComplete = () => {
  // repopulate access token on global access variable
  window.sessionState = store.getState().loggedUserState.sessionState;
};

export const persistor = persistStore(store, null, onRehydrateComplete);
export default store;
